import { useState, useEffect, useRef } from 'react';
import config from '@/config';
import { useMapStore } from '@/stores/mapStore';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { gridKeyForLocation } from '@/utils/grid';

// WebSocket subprotocol used to carry the auth token to traffic-service. Passing
// it as a subprotocol (not a ?query= param) keeps the token out of server access
// logs. Must match wsAuthProto in backend/traffic-service.
const WS_AUTH_PROTO = 'tm-auth';

// Personal (client-side) mute list of author ids — you stop seeing these authors.
// Persisted so a mute survives reloads. Separate from the server-side auto-mute
// that stops a reported author from posting for everyone.
const MUTED_AUTHORS_KEY = 'geochat-muted-authors';
function loadMutedAuthors(): string[] {
  try {
    const raw = localStorage.getItem(MUTED_AUTHORS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

interface ChatMessage {
  type: 'chat';
  text: string;
  sender: string;
  timestamp: string;
  authorId?: string; // server-stamped stable author id (for report/mute)
}

export function GeoChat() {
  const anyPanelOpen = useUIStore((s) => Object.entries(s.panels).some(([k, v]) => v && k !== 'searchPanel'));
  const openPanel = useUIStore((s) => s.openPanel);
  const addNotification = useUIStore((s) => s.addNotification);
  // GeoChat is registered-users-only (anti-abuse): the WS is refused without a
  // valid token, and the UI shows a sign-in prompt instead of the chat.
  const isAuthed = useAuthStore((s) => !!s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const [isOpen, setIsOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [mutedAuthors, setMutedAuthors] = useState<string[]>(loadMutedAuthors);

  const muteAuthor = (authorId: string) => {
    setMutedAuthors((prev) => {
      if (prev.includes(authorId)) return prev;
      const next = [...prev, authorId];
      try {
        localStorage.setItem(MUTED_AUTHORS_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable — mute stays in-memory for this session */
      }
      return next;
    });
    addNotification({ type: 'info', message: "Muted — you won't see this user's messages.", duration: 3000 });
  };

  const reportAuthor = (authorId: string, text: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      // Send the reported message text so the server can capture it as durable
      // moderation evidence (messages themselves are ephemeral).
      wsRef.current.send(JSON.stringify({ type: 'report', target: authorId, text }));
    }
  };
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Generate a random sender ID for this session. Uses a wide random suffix so two
  // people in the same grid room don't collide onto one id — a collision would make
  // one person's messages render as the other's ("you"/right-aligned).
  const senderId = useRef(`user-${Math.random().toString(36).slice(2, 8)}`).current;

  // Grid room derived from the live map center. Re-derives whenever the map
  // moves; the value only actually changes when the center crosses a cell
  // boundary, so the connect effect below reconnects only when the user moves
  // into a different neighborhood (panning within a cell does nothing).
  const mapCenter = useMapStore((s) => s.viewport.center);
  const gridKey = gridKeyForLocation(mapCenter.lat, mapCenter.lng);

  useEffect(() => {
    const clearReconnect = () => {
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = null;
      }
    };

    // Closed or no valid grid yet: tear down. GeoChat is read-open — anyone may
    // connect to read; posting is gated separately (below + server-side).
    if (!isOpen || !gridKey) {
      clearReconnect();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    // Moving to a new grid cell means a new room: clear the previous cell's
    // messages so the conversation reflects where the user actually is.
    setMessages([]);
    // Reset connection state on every (re)connect so the UI doesn't show a
    // stale "connected" dot during the gap between a grid change and the new
    // socket's onopen.
    setIsConnected(false);

    let disposed = false;
    let attempts = 0;
    const wsUrl = `${config.api.websocketUrl}/traffic/ws?grid=${encodeURIComponent(gridKey)}`;

    const connect = () => {
      if (disposed) return;
      reconnectRef.current = null;
      // Signed-in users carry their token as a subprotocol so the server grants
      // POST rights (kept out of logs vs a ?query= param); anonymous users connect
      // without one and can read but not post.
      const ws = accessToken
        ? new WebSocket(wsUrl, [WS_AUTH_PROTO, accessToken])
        : new WebSocket(wsUrl);
      wsRef.current = ws;
      // Guards below ignore events from a socket that is no longer the current
      // one — prevents a lingering old socket (after a grid change) from
      // mutating state or appending stale messages.
      ws.onopen = () => {
        if (wsRef.current !== ws) return;
        attempts = 0;
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        if (wsRef.current !== ws) return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'chat') {
            setMessages((prev) => [...prev, data]);
          } else if (data.type === 'report_ack') {
            addNotification({
              type: 'info',
              message: data.muted
                ? 'Reported. This user has been muted for everyone after multiple reports.'
                : 'Reported — thanks for helping keep GeoChat clean.',
              duration: 4000,
            });
          } else if (data.type === 'error') {
            // Server refused a post (rate_limited / muted / auth_required). Surface
            // it so the user knows the message didn't send.
            addNotification({
              type: 'info',
              message: data.message || 'Message not sent.',
              duration: 4000,
            });
          }
        } catch (e) {
          console.error('Failed to parse chat message', e);
        }
      };

      ws.onclose = () => {
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        setIsConnected(false);
        if (disposed) return;
        // Reconnect to the same room with capped exponential backoff.
        attempts += 1;
        const delay = Math.min(1000 * 2 ** (attempts - 1), 10000);
        reconnectRef.current = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      disposed = true;
      clearReconnect();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isOpen, gridKey, isAuthed, accessToken, addNotification]);

  useEffect(() => {
    // Scroll to bottom
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = () => {
    if (!inputText.trim() || wsRef.current?.readyState !== WebSocket.OPEN) return;

    const msg = {
      type: 'chat',
      text: inputText,
      sender: senderId,
    };

    wsRef.current.send(JSON.stringify(msg));
    setInputText('');
  };

  // Hide messages from authors this user has personally muted (applies to live
  // messages and replayed history alike).
  const visibleMessages = messages.filter((m) => !(m.authorId && mutedAuthors.includes(m.authorId)));

  return (
    <div className={`absolute bottom-40 right-4 z-controls ${anyPanelOpen ? 'max-md:hidden' : ''}`}>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-full shadow-lg flex items-center justify-center w-12 h-12"
          title="GeoChat — chat live with people near this spot on the map"
          aria-label="Open GeoChat: chat with people in this area"
        >
          💬
        </button>
      )}

      {isOpen && (
        <div className="bg-white rounded-lg shadow-xl w-80 max-w-[calc(100vw-2rem)] h-96 max-h-[70vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-blue-500 text-white p-3 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="font-bold">GeoChat</span>
              <span
                className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`}
                title={isConnected ? 'Connected to this area' : 'Not connected'}
              />
              <button
                onClick={() => setShowInfo((v) => !v)}
                className="flex h-5 w-5 items-center justify-center rounded-full border border-white/60 text-[11px] font-bold leading-none hover:bg-white/20"
                title="What is GeoChat?"
                aria-label="What is GeoChat?"
                aria-expanded={showInfo}
              >
                i
              </button>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white hover:text-gray-200" aria-label="Close GeoChat">
              ✕
            </button>
          </div>

          {/* Purpose explainer (toggled by the info icon) */}
          {showInfo && (
            <div className="border-b border-blue-100 bg-blue-50 p-3 text-xs leading-relaxed text-gray-700">
              <p className="mb-1 font-semibold text-gray-900">Chat with people near this spot</p>
              GeoChat is a live, location-based chat for the part of the map you're
              viewing. Everyone looking at the same neighborhood shares one room —
              pan to a new area and you join that area's chat. Great for local
              questions, road conditions, or meetups. Messages are public within
              the area and not saved to your account.
            </div>
          )}

          {/* Messages (authors you've personally muted are hidden) */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
            {visibleMessages.length === 0 && (
              <div className="text-center text-gray-400 text-sm mt-4">
                No messages nearby. Say hello!
              </div>
            )}
            {visibleMessages.map((msg, i) => {
              const isMe = msg.sender === senderId;
              return (
                <div key={i} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                  <div
                    className={`max-w-[80%] rounded-lg p-2 text-sm ${
                      isMe ? 'bg-blue-500 text-white' : 'bg-white border border-gray-200 text-gray-800'
                    }`}
                  >
                    {!isMe && <div className="text-xs text-gray-500 mb-1">{msg.sender}</div>}
                    {msg.text}
                  </div>
                  {/* Report / mute actions on other people's messages */}
                  {!isMe && msg.authorId && (
                    <div className="mt-0.5 flex gap-2 px-1 text-[11px] text-gray-400">
                      <button onClick={() => muteAuthor(msg.authorId!)} className="hover:text-gray-600" title="Hide this user's messages for you">
                        Mute
                      </button>
                      {isAuthed && (
                        <button onClick={() => reportAuthor(msg.authorId!, msg.text)} className="hover:text-red-500" title="Report this message to moderators">
                          Report
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input — signed-in users can post; anonymous users read-only and are
              prompted to sign in (posting is also enforced server-side). */}
          {isAuthed ? (
            <div className="p-3 bg-white border-t border-gray-200 flex gap-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Type a message..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                disabled={!isConnected}
              />
              <button
                onClick={sendMessage}
                disabled={!isConnected || !inputText.trim()}
                className="bg-blue-500 text-white p-2 rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                ➤
              </button>
            </div>
          ) : (
            <div className="p-3 bg-white border-t border-gray-200 flex items-center gap-2 text-sm text-gray-600">
              <span className="flex-1">Sign in to post</span>
              <button
                onClick={() => {
                  setIsOpen(false);
                  openPanel('accountPanel');
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-md font-medium whitespace-nowrap"
              >
                Sign in
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
